import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import  LogoContainer from '../../../../../shared/components/LogoContainer'
import { currencyOptions } from '../../../../../../utils/constants/currencyOptions';
import { createIncome } from '../../../../../../app/store/slices/incomeSlice';
import { selectAccounts, fetchAccounts } from '../../../../../../app/store/slices/accountSlice';
import { INCOME_SOURCES } from '../../../../../../utils/constants/dashboardConstants'
import  AddAnotherList from '../../../../../shared/components/AddAnotherList';
import  Spinner  from '../../../../../shared/components/Spinner';
import {formatAmount} from '../../../../../../utils/formatAmount';

const IncomeSetup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const accounts = useSelector(selectAccounts);

  const [incomes, setIncomes] = useState([]);
  const [currentIncome, setCurrentIncome] = useState({
    amount: '',
    source: '',
    currency: location.state?.currency || JSON.parse(localStorage.getItem('onboardingCanvas'))?.currency,
    account: location.state?.accountId || ''
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedCanvasId, setSelectedCanvasId] = useState(
    JSON.parse(localStorage.getItem('onboardingCanvas'))?._id ||
    location.state?.canvasId
  );

  // Fetch accounts for the selected canvas
  useEffect(() => {
    if (selectedCanvasId) {
      dispatch(fetchAccounts({ canvasId: selectedCanvasId, limit:1000 }));
    }
  }, [dispatch, selectedCanvasId]);

  useEffect(() => {
    if (!selectedCanvasId && location.state?.canvasId) {
      setSelectedCanvasId(location.state.canvasId);
    }
  }, [location.state, selectedCanvasId]);

  //Handle Input Values Change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentIncome({ ...currentIncome, [name]: value });
  };

  //Handle Add Multiple Income
  const handleAddIncome = async () => {
    if (
      currentIncome.amount &&
      currentIncome.source &&
      currentIncome.currency &&
      currentIncome.account &&
      selectedCanvasId
    ) {
      try {
        await dispatch(createIncome({
          canvasId: selectedCanvasId,
          incomeData: {
            name: 'Income',
            amount: Number(currentIncome.amount),
            source: currentIncome.source,
            frequency: 'Monthly',
            currency: currentIncome.currency,
            account: currentIncome.account
          }
        })).unwrap();
        setIncomes([...incomes, currentIncome]);
        setCurrentIncome({
          amount: '',
          source: '',
          currency: location.state?.currency || '',
          account: location.state?.accountId || ''
        });
      } catch (err) {
        console.error('Failed to create income:', err);
      }
    }
  };

  //Handle Continue
  const handleContinue = async () => {
    setIsProcessing(true);
    try {
      if (
        currentIncome.amount &&
        currentIncome.source &&
        currentIncome.currency &&
        currentIncome.account &&
        selectedCanvasId
      ) {
        await dispatch(createIncome({
          canvasId: selectedCanvasId,
          incomeData: {
            name: 'Income',
            amount: Number(currentIncome.amount),
            source: currentIncome.source,
            frequency: 'Monthly',
            currency: currentIncome.currency,
            account: currentIncome.account
          }
        })).unwrap();
        setIncomes(prev => [...prev, currentIncome]);
        setCurrentIncome({
          amount: '',
          source: '',
          currency: location.state?.currency || '',
          account: location.state?.accountId || ''
        });
      }
       navigate('/budget', { state: { accountId: currentIncome.account, canvasId: selectedCanvasId } });
    } catch (err) {
      console.error('Failed to create income:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
     <LogoContainer/>
      <div className='card-setup'>
        <h2 className='txt-page-heading'>Enter your Income</h2>
        <p className="p-tag-below-heading text-center">
          Enter your monthly fixed recurring income
        </p>
        <div className="w-full flex flex-col items-center">
          <div className="space-y-4 w-full max-w-lg mx-auto">
            {/* Monthly Amount */}
            <div className="flex items-center justify-center gap-3 w-full">
              <label htmlFor="incomeAmount" className="sm:w-44 w-32 text-left font-medium text-gray-700 text-base">
                Monthly Amount
              </label>
              <input
                type="number"
                id="incomeAmount"
                name="amount"
                value={currentIncome.amount}
                onChange={handleInputChange}
                placeholder="Enter Amount"
                className="input-field flex-1 w-full max-w-[400px]"
                min="0"
                step="0.01"
              />
            </div>
            {/* Income Source */}
            <div className="flex items-center justify-center gap-3 w-full">
              <label htmlFor="incomeSource" className="sm:w-44 w-32 text-left font-medium text-gray-700 text-base">
                Income Source
              </label>
              <select
                id="incomeSource"
                name="source"
                value={currentIncome.source}
                onChange={handleInputChange}
                className="input-field flex-1 w-full max-w-[400px]  text-gray-500"
              >
                <option value="">eg. Salary</option>
                {INCOME_SOURCES.map((src) => (
                  <option key={src.value} value={src.value}>
                    {src.label}
                  </option>
                ))}
              </select>
            </div>
            {/* Currency */}
            <div className="flex items-center justify-center gap-3 w-full">
              <label htmlFor="currency" className="sm:w-44 w-32 text-left font-medium text-gray-700 text-base">
                Currency
              </label>
              <select
                id="currency"
                name="currency"
                value={currentIncome.currency}
                onChange={handleInputChange}
                className="input-field flex-1 w-full max-w-[400px]  text-gray-500"
              >
                <option value="">Select Currency</option>
                {currencyOptions.map((currency) => (
                  <option key={currency.code} value={currency.code}>
                    {currency.code} - {currency.name} ({currency.symbol})
                  </option>
                ))}
              </select>
            </div>
            {/* Deposit Account */}
            <div className="flex items-center justify-center gap-3 w-full">
              <label htmlFor="account" className="sm:w-44 w-32 text-left font-medium text-gray-700 text-base">
                Deposit Account
              </label>
              <select
                id="account"
                name="account"
                value={currentIncome.account}
                onChange={handleInputChange}
                className="input-field flex-1 w-full max-w-[400px] text-gray-500"
                required
              >
                <option value="">Select Account</option>
                {accounts.map(acc => (
                  <option key={acc._id} value={acc._id}>
                    {acc.name} ({acc.currency})
                  </option>
                ))}
              </select>
            </div>
          </div>
          {/* Centered action buttons */}
          <div className="mt-8 flex flex-row justify-center gap-6 w-full max-w-lg">
            <button
              type="button"
              className="btn-back flex-1"
              onClick={() => {
                setCurrentIncome({ amount: '', source: '', currency: location.state?.currency || '', account: location.state?.accountId || '' });
                navigate('/budget', { state: { accountId: currentIncome.account, canvasId: selectedCanvasId } });
              }}
            >
              Skip
            </button>
            <button
              type="button"
              className="btn-continue flex-1"
              onClick={handleAddIncome}
            >
              Add Other Income
            </button>
          </div>
        </div>

       <AddAnotherList
          title="Your Incomes"
          items={incomes}
          renderItem={(income) => (
            <>
              <div>
                <div className="font-medium">{income.source}</div>
                <div className="text-sm text-gray-500">{formatAmount(income.amount)}</div>
              </div>
              <div className="text-sm text-gray-500">
                {income.currency || 'No currency selected'}
              </div>
            </>
          )}
        />

        <div className="flex flex-col md:flex-row justify-between gap-4 mt-8">
          <button
            className="btn-back"
            onClick={() => navigate(-1)}
          >
            Back
          </button>

          <button
            className="btn-continue"
            onClick={handleContinue}
          >
            {isProcessing ? (
              <Spinner text="Processing..."/>
            ) : (
              'Save & Continue'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default IncomeSetup;